import { SidebarSimpleIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { AFTER_CHAT_HISTORY_TRANSITION_DELAY_CLASS } from "@/components/herald-chat/herald-chat-history-sidebar/chat-history-sidebar.constants";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import useHeraldChatSettingsStore from "@/store/herald-chat/herald-chat-settings.slice";

export interface HeraldChatControlPanelProps {
  hasHistoryControl: boolean;
  isHistorySidebarExpanded: boolean;
  onShowHistory: () => void;
  /** Active conversation title; omitted for new / untitled chats. */
  conversationTitle?: string;
}

const HeraldChatControlPanel = ({
  hasHistoryControl,
  isHistorySidebarExpanded,
  onShowHistory,
  conversationTitle,
}: HeraldChatControlPanelProps) => {
  const isHeraldChatEmpty = useHeraldChatSettingsStore(
    (state) => state.isHeraldChatEmpty,
  );
  const showHistoryButton = hasHistoryControl && !isHistorySidebarExpanded;
  const title = conversationTitle?.trim() ?? "";
  // Fade Open in only after the sidebar finishes collapsing; skip it when the page loads collapsed.
  const [hasSeenHistorySidebarExpanded, setHasSeenHistorySidebarExpanded] =
    useState(!showHistoryButton);
  if (!showHistoryButton && !hasSeenHistorySidebarExpanded) {
    setHasSeenHistorySidebarExpanded(true);
  }

  // Empty landing: Open when sidebar collapsed; hide entirely when expanded.
  if (isHeraldChatEmpty && !showHistoryButton) {
    return null;
  }

  return (
    <div className="relative z-10 flex w-full min-w-0 shrink-0 items-center px-4.5 pt-4.5 pb-1.5">
      {showHistoryButton ? (
        <div
          className={cn(
            "-m-2 shrink-0 overflow-visible p-2",
            hasSeenHistorySidebarExpanded &&
              `animate-in fade-in fill-mode-backwards ${AFTER_CHAT_HISTORY_TRANSITION_DELAY_CLASS}`,
          )}
        >
          <TooltipTrigger>
            <Button
              onPress={onShowHistory}
              variant="outline"
              size="sm"
              aria-label="Open sidebar"
              className="flex items-center gap-1 rounded-3xl shadow-none drop-shadow-[0_0_12px_rgba(105,29,211,0.16)]"
            >
              <SidebarSimpleIcon className="h-4 w-4 shrink-0 text-gray-500" />
              Open
            </Button>
            <Tooltip placement="bottom start" offset={15} crossOffset={35}>
              Open sidebar
            </Tooltip>
          </TooltipTrigger>
        </div>
      ) : null}
      {/* Title is left-aligned to the chat pane: 32px in (px-4.5 + pl-3.5), or just right of Open. */}
      <div
        className={cn(
          "flex h-9 min-w-0 flex-1 items-center",
          showHistoryButton ? "pl-3" : "pl-3.5",
        )}
      >
        {title || !isHeraldChatEmpty ? (
          <p
            aria-hidden={title ? undefined : true}
            className={cn(
              "min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-normal",
              title ? "text-gray-700" : "invisible",
            )}
          >
            {title || "\u00a0"}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default HeraldChatControlPanel;
